import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import {
  AnfrageWizardContext,
  type AnfrageWizardChangedHandler,
  type AnfrageWizardContextValue,
  type AnfrageWizardNoticeHandler,
} from "@/pages/anfragen/anfrageWizardContextValue";
import { AnfrageWizardHost, type AnfrageWizardHostHandle } from "@/pages/anfragen/AnfrageWizardHost";

export function AnfrageWizardProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const hostRef = useRef<AnfrageWizardHostHandle>(null);

  const noticeListenersRef = useRef<Set<AnfrageWizardNoticeHandler>>(new Set());
  const changedListenersRef = useRef<Set<AnfrageWizardChangedHandler>>(new Set());

  const emitNotice = useCallback((message: string) => {
    noticeListenersRef.current.forEach((listener) => {
      try {
        listener(message);
      } catch (err) {
        console.warn("[AnfrageWizardProvider] notice listener failed", err);
      }
    });
  }, []);

  const emitChanged = useCallback(() => {
    changedListenersRef.current.forEach((listener) => {
      try {
        listener();
      } catch (err) {
        console.warn("[AnfrageWizardProvider] changed listener failed", err);
      }
    });
  }, []);

  const openNew = useCallback(() => {
    hostRef.current?.openNew();
  }, []);

  const openDraft = useCallback((draft: Parameters<AnfrageWizardHostHandle["openDraft"]>[0]) => {
    hostRef.current?.openDraft(draft);
  }, []);

  const openDraftById = useCallback(async (id: number, opts?: { sourceMaId?: number; title?: string }) => {
    await hostRef.current?.openDraftById(id, opts);
  }, []);

  const subscribeNotice = useCallback((listener: AnfrageWizardNoticeHandler) => {
    noticeListenersRef.current.add(listener);
    return () => {
      noticeListenersRef.current.delete(listener);
    };
  }, []);

  const subscribeAnfragenChanged = useCallback((listener: AnfrageWizardChangedHandler) => {
    changedListenersRef.current.add(listener);
    return () => {
      changedListenersRef.current.delete(listener);
    };
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
  }, []);

  const value = useMemo<AnfrageWizardContextValue>(
    () => ({
      openNew,
      openDraft,
      openDraftById,
      isOpen,
      subscribeNotice,
      subscribeAnfragenChanged,
    }),
    [openNew, openDraft, openDraftById, isOpen, subscribeNotice, subscribeAnfragenChanged],
  );

  return (
    <AnfrageWizardContext.Provider value={value}>
      {children}
      <AnfrageWizardHost
        ref={hostRef}
        onOpenChange={handleOpenChange}
        emitNotice={emitNotice}
        emitChanged={emitChanged}
      />
    </AnfrageWizardContext.Provider>
  );
}
